import React, { useEffect, useState } from "react";
import client, { apiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { rupiah, dateTimeFmt } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ReceiptView } from "@/pages/POS";
import { Eye, Printer, AlertTriangle, Undo2, RotateCcw } from "lucide-react";

export default function Sales() {
  const { user } = useAuth();
  const { effectiveBranch, currentBranchName, branches } = useBranch();
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [retFor, setRetFor] = useState(null);
  const [retQty, setRetQty] = useState({});
  const [retReason, setRetReason] = useState("");

  const canReturn = ["super_admin", "admin", "branch_head"].includes(user.role);
  const hideCost = ["branch_head", "cashier"].includes(user.role);
  const detailBranch = (detail && branches.find((b) => b.id === detail.branch_id)?.name) || currentBranchName;

  const load = () => client.get("/sales", { params: effectiveBranch ? { branch_id: effectiveBranch } : {} }).then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [effectiveBranch]);

  const openReturn = (s) => {
    setRetFor(s);
    const init = {}; s.items.forEach((_, i) => { init[i] = ""; });
    setRetQty(init); setRetReason(""); setDetail(null);
  };

  const submitReturn = async () => {
    const items = retFor.items
      .map((it, i) => ({ product_id: it.product_id, unit: it.unit, qty: Number(retQty[i]) || 0 }))
      .filter((x) => x.qty > 0);
    if (!items.length) { toast.error("Isi jumlah retur minimal 1 item"); return; }
    try {
      const r = await client.post("/returns", { sale_id: retFor.id, items, reason: retReason });
      toast.success(`Retur ${r.data.number} — refund ${rupiah(r.data.refund_total)}, stok dikembalikan`);
      setRetFor(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 space-y-5 animate-in-up">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Penjualan</h1>
        <p className="text-sm text-muted-foreground">{user.role === "cashier" ? "Riwayat transaksi Anda" : "Riwayat transaksi"} · {currentBranchName}</p>
      </div>
      <div className="rounded-lg border bg-white">
        <Table>
          <TableHeader><TableRow>
            <TableHead>No</TableHead><TableHead>Waktu</TableHead><TableHead>Kasir</TableHead>
            <TableHead className="text-right">Total</TableHead>{!hideCost && <TableHead className="text-right">Laba</TableHead>}<TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((s) => (
              <TableRow key={s.id} className="hover:bg-slate-50" data-testid={`sale-row-${s.id}`}>
                <TableCell className="font-mono text-xs">{s.number}
                  {s.flagged_negative && <Badge className="ml-2 bg-rose-100 text-rose-700"><AlertTriangle className="mr-1 h-3 w-3" />override</Badge>}
                  {s.has_return && <Badge className="ml-2 bg-orange-100 text-orange-700"><RotateCcw className="mr-1 h-3 w-3" />retur</Badge>}
                </TableCell>
                <TableCell className="text-xs">{dateTimeFmt(s.created_at)}</TableCell>
                <TableCell className="text-sm">{s.cashier_name}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{rupiah(s.total)}</TableCell>
                {!hideCost && <TableCell className="text-right font-mono text-emerald-600">{rupiah(s.profit)}</TableCell>}
                <TableCell><Button size="icon" variant="ghost" onClick={() => setDetail(s)} data-testid={`view-sale-${s.id}`}><Eye className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={hideCost ? 5 : 6} className="py-10 text-center text-muted-foreground">Belum ada transaksi</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Detail Transaksi</DialogTitle></DialogHeader>
          {detail && (
            <>
              <div className="text-center text-xs text-muted-foreground">Struk termal 58mm</div>
              <div className="flex justify-center"><ReceiptView receipt={detail} branchName={detailBranch} width="58" cashier={detail.cashier_name} /></div>
              <DialogFooter className="gap-2 sm:flex-row">
                {canReturn && <Button variant="outline" className="flex-1" onClick={() => openReturn(detail)} data-testid="open-return-btn"><Undo2 className="mr-2 h-4 w-4" /> Proses Retur</Button>}
                <Button className="flex-1" onClick={() => window.print()} data-testid="reprint-btn"><Printer className="mr-2 h-4 w-4" /> Cetak Ulang</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {detail && (
        <div id="receipt-print" className="hidden print:block"><ReceiptView receipt={detail} branchName={detailBranch} width="58" cashier={detail.cashier_name} plain /></div>
      )}

      <Dialog open={!!retFor} onOpenChange={(o) => !o && setRetFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Retur Penjualan</DialogTitle>
            <DialogDescription>{retFor?.number} · isi jumlah yang diretur per item (stok dikembalikan & laba berkurang)</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {retFor?.items.map((it, i) => (
              <div key={i} className="flex items-center gap-3 rounded-md border p-2" data-testid={`return-item-${i}`}>
                <div className="flex-1">
                  <div className="text-sm font-medium">{it.name}</div>
                  <div className="text-xs text-muted-foreground">terjual {it.qty} {it.unit} · {rupiah(it.sell_price)}</div>
                </div>
                <Input type="number" min="0" max={it.qty} value={retQty[i] ?? ""} placeholder="0"
                  onChange={(e) => setRetQty((s) => ({ ...s, [i]: e.target.value }))}
                  className="w-24 font-mono" data-testid={`return-qty-${i}`} />
              </div>
            ))}
            <div className="space-y-2 pt-1">
              <Label>Alasan</Label>
              <Input value={retReason} onChange={(e) => setRetReason(e.target.value)} placeholder="mis. barang rusak / salah beli" data-testid="return-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetFor(null)}>Batal</Button>
            <Button onClick={submitReturn} data-testid="submit-return-btn"><Undo2 className="mr-2 h-4 w-4" /> Proses Retur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
